import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import Navbar from "../Navbar/Navbar";
import { createOrder } from "../../services/orders.service";

const YourOrder = () => {
  const { items, increaseQty, decreaseQty, removeItem, clearCart } = useCart();
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [orderSent, setOrderSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    address: "",
  });

  const total = useMemo(() => {
    return items.reduce((sum, i) => sum + i.price * (i.quantity || 1), 0);
  }, [items]);

  const handleInputChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setError("");
  };

  const validateForm = () => {
    const f = form;
    if (!f.firstName.trim()) return "Emri është i detyrueshëm.";
    if (!f.lastName.trim()) return "Mbiemri është i detyrueshëm.";
    if (!f.phone.trim()) return "Numri i telefonit është i detyrueshëm.";
    if (!f.address.trim()) return "Adresa është e detyrueshme.";
    return null;
  };

  const handleConfirmOrder = async (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) {
      setError(err);
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      await createOrder({
        customer: form,
        items,
        total,
      });
      clearCart();
      setOrderSent(true);
    } catch (err) {
      setError(err?.message || "Ndodhi një gabim. Provoni përsëri.");
    } finally {
      setSubmitting(false);
    }
  };

  if (orderSent) {
    return (
      <div className="min-h-screen bg-[#e0d8c3]/20">
        <Navbar />
        <div className="py-10 px-4">
          <div className="mx-auto max-w-lg text-center">
            <div className="rounded-2xl border border-[#a6a6a6]/30 bg-[#ffffff] p-8 shadow-lg">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#e0d8c3]/50 text-[#000000] text-2xl">
                ✓
              </div>
              <h1 className="text-xl font-bold text-[#000000]">
                Porosia u dërgua
              </h1>
              <p className="mt-2 text-[#a6a6a6]">
                Ju faleminderit. Porosia juaj është regjistruar. Pagesa bëhet në
                dorëzim kur të merrni porosinë.
              </p>
              <Link
                to="/"
                className="mt-6 inline-block w-full rounded-xl bg-[#000000] py-3 font-medium text-[#ffffff] hover:bg-[#000000]/90 transition"
              >
                Kthehu te katalogu
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#e0d8c3]/20">
      <Navbar />
      <div className="py-10 px-4">
        <div className="mx-auto max-w-4xl">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-[#000000]">
              Porosia juaj
            </h1>
            <Link
              to="/"
              className="mt-2 inline-block text-sm font-medium text-[#a6a6a6] hover:text-[#000000] hover:underline"
            >
              Keni harruar diçka? Kthehuni te katalogu
            </Link>
          </div>

          {items.length === 0 ? (
            <div className="rounded-2xl border border-[#a6a6a6]/30 bg-[#ffffff] p-8 text-center text-[#a6a6a6]">
              Shporta është bosh.
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2 space-y-4">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-4 rounded-2xl border border-[#a6a6a6]/30 bg-[#ffffff] p-4 shadow-sm"
                  >
                    <div className="h-20 w-20 flex items-center justify-center rounded-xl bg-[#e0d8c3]/30 overflow-hidden shrink-0 border border-[#a6a6a6]/20">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className="h-full w-full object-contain"
                          loading="lazy"
                        />
                      ) : (
                        <span className="text-xs text-[#a6a6a6]">Pa foto</span>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#000000]">
                        {item.name}
                      </h3>
                      <p className="mt-1 text-sm font-medium text-[#000000]">
                        €{Number(item.price).toFixed(2)}
                      </p>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <div className="flex items-center rounded-xl border border-[#a6a6a6]/40">
                        <button
                          type="button"
                          onClick={() => decreaseQty(item.id)}
                          className="px-3 py-1 text-lg hover:bg-[#e0d8c3]/30 text-[#000000]"
                        >
                          −
                        </button>
                        <span className="px-3 text-sm font-medium text-[#000000] min-w-[2rem] text-center">
                          {item.quantity || 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => increaseQty(item.id)}
                          className="px-3 py-1 text-lg hover:bg-[#e0d8c3]/30 text-[#000000]"
                        >
                          +
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="text-sm text-[#a6a6a6] hover:text-[#000000] hover:underline"
                      >
                        Hiqe
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl border border-[#a6a6a6]/30 bg-[#ffffff] p-5 shadow-sm">
                  <h2 className="mb-4 text-lg font-semibold text-[#000000]">
                    Përmbledhje
                  </h2>

                  <div className="space-y-2 text-sm text-[#a6a6a6]">
                    {items.map((i) => (
                      <div key={i.id} className="flex justify-between text-[#000000]">
                        <span>
                          {i.quantity || 1}× {i.name}
                        </span>
                        <span>
                          €{((i.price || 0) * (i.quantity || 1)).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="my-4 h-px bg-[#a6a6a6]/30" />

                  <div className="flex justify-between text-lg font-bold text-[#000000]">
                    <span>Total</span>
                    <span>
                      €{total.toFixed(2)}
                    </span>
                  </div>

                  {!showCheckoutForm ? (
                    <button
                      type="button"
                      onClick={() => setShowCheckoutForm(true)}
                      className="mt-5 w-full rounded-xl bg-[#000000] py-2.5 font-medium text-[#ffffff] hover:bg-[#000000]/90 transition"
                    >
                      Vazhdo te pagesa
                    </button>
                  ) : (
                    <>
                      <p className="mt-4 text-sm font-medium text-[#000000]">
                        Të dhënat tuaja (pagesa në dorëzim)
                      </p>

                      <form
                        onSubmit={handleConfirmOrder}
                        className="mt-3 space-y-3"
                      >
                        <div>
                          <label className="mb-1 block text-sm text-[#a6a6a6]">
                            Emri *
                          </label>
                          <input
                            type="text"
                            required
                            value={form.firstName}
                            onChange={(e) =>
                              handleInputChange("firstName", e.target.value)
                            }
                            className="w-full rounded-xl border border-[#a6a6a6]/50 px-3 py-2.5 text-[#000000] focus:ring-2 focus:ring-[#e0d8c3] focus:border-[#e0d8c3] bg-white"
                            placeholder="Emri juaj"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-sm text-[#a6a6a6]">
                            Mbiemri *
                          </label>
                          <input
                            type="text"
                            required
                            value={form.lastName}
                            onChange={(e) =>
                              handleInputChange("lastName", e.target.value)
                            }
                            className="w-full rounded-xl border border-[#a6a6a6]/50 px-3 py-2.5 text-[#000000] focus:ring-2 focus:ring-[#e0d8c3] focus:border-[#e0d8c3] bg-white"
                            placeholder="Mbiemri juaj"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-sm text-[#a6a6a6]">
                            Telefoni *
                          </label>
                          <input
                            type="tel"
                            required
                            value={form.phone}
                            onChange={(e) =>
                              handleInputChange("phone", e.target.value)
                            }
                            className="w-full rounded-xl border border-[#a6a6a6]/50 px-3 py-2.5 text-[#000000] focus:ring-2 focus:ring-[#e0d8c3] focus:border-[#e0d8c3] bg-white"
                            placeholder="p.sh. 044 123 456"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-sm text-[#a6a6a6]">
                            Adresa *
                          </label>
                          <textarea
                            required
                            rows={3}
                            value={form.address}
                            onChange={(e) =>
                              handleInputChange("address", e.target.value)
                            }
                            className="w-full rounded-xl border border-[#a6a6a6]/50 px-3 py-2.5 text-[#000000] focus:ring-2 focus:ring-[#e0d8c3] focus:border-[#e0d8c3] resize-none bg-white"
                            placeholder="Rruga, ndërtesa, qyteti..."
                          />
                        </div>

                        {error && (
                          <p className="text-sm text-red-600">{error}</p>
                        )}

                        <button
                          type="submit"
                          disabled={submitting}
                          className="w-full rounded-xl bg-[#000000] py-2.5 font-medium text-[#ffffff] hover:bg-[#000000]/90 transition disabled:opacity-60"
                        >
                          {submitting
                            ? "Duke dërguar..."
                            : "Konfirmo porosinë (pagesa në dorëzim)"}
                        </button>
                      </form>

                      <button
                        type="button"
                        onClick={() => setShowCheckoutForm(false)}
                        className="mt-2 w-full text-sm text-[#a6a6a6] hover:text-[#000000] hover:underline"
                      >
                        Kthehu
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default YourOrder;
